<template>
  <q-card flat class="q-pa-md">
    <div class="text-h6 q-py-xs" :class="$q.dark.mode ? '' : 'text-primary'">
      Player Favourite Stock<span class="text-caption text-bold">
        (It captures players favourite stock)</span
      >
      <div class="text-caption text-bold q-pa-xs">
        As of : {{ convertDate(store.state.market.asOfLive) }}
      </div>
    </div>
    <PackageAuth>
      <template #authorized-content>
        <div class="q-gutter-sm">
          <q-radio
            keep-color
            v-model="filterPlayerFavouriteBuySell"
            val="show_all"
            label="Show All"
            color="primary"
          >
            <q-tooltip>Show All</q-tooltip>
          </q-radio>

          <q-radio
            keep-color
            v-model="filterPlayerFavouriteBuySell"
            val="player_favourite_buy"
            label="Player Favourite Buy"
            color="teal"
          >
            <q-tooltip>Player Favourite Buy</q-tooltip>
          </q-radio>

          <q-radio
            keep-color
            v-model="filterPlayerFavouriteBuySell"
            val="player_favourite_sell"
            label="Player Favourite Sell"
            color="red"
          >
            <q-tooltip>Player Favourite Sell</q-tooltip>
          </q-radio>

          <q-checkbox
            dense
            class="q-pl-sm ellipsis"
            v-model="mySignals"
            color="blue-5"
            keep-color
            label="My Signals"
          >
            <q-tooltip class="bg-primary">My Signals Only</q-tooltip>
          </q-checkbox>
        </div>

        <q-table
          flat
          bordered
          :rows="
            filterPlayerFavouriteBuySell === 'show_all'
              ? computedBulkTransactionData
              : finalComputedData
          "
          :columns="computedColumns"
          :pagination="pagination"
          separator="cell"
          binary-state-sort
          :filter="filterSymbol"
          :filter-method="(rows) => filterSymbolData(rows, filterSymbol)"
          :class="
            $q.dark.mode
              ? 'table-sticky-header-column-dark'
              : 'table-sticky-header-column'
          "
        >
          <template v-slot:top-left>
            <q-select
              outline
              v-model="selectedSector"
              :options="optionSectors"
              dense
              label="Choose sector"
              single
              input-debounce="0"
              option-label="name"
              option-value="symbol"
              class="ellipsis q-mr-sm"
              :menu-absolute="true"
              style="width: 150px"
            />
            <StockSearch
              :allStocks="allStocks"
              @emitededSymbol="
                (eSymbol) => {
                  filterSymbol = eSymbol;
                }
              "
            />
            <q-select
              v-if="isUserPlatinum"
              outline
              v-model="strength"
              :options="[
                'All',
                'Normal',
                'Strong',
                'Very Strong',
                'Very Very Strong',
              ]"
              dense
              label="Strength"
              single
              class="q-mx-sm"
              style="width: 150px"
              :menu-absolute="true"
            />
          </template>

          <template v-slot:body-cell-symbol="props">
            <q-td
              :props="props"
              :class="
                props.row.bulk_status === 'SELL'
                  ? 'bg-red-5 text-white'
                  : props.row.bulk_status === 'BUY'
                  ? 'bg-green-5 text-white'
                  : 'bg-blue-5 text-white'
              "
            >
              <q-item dense clickable @click="onClickSymbolEmit(props.value)">
                <div class="col">
                  {{ props.value }}
                  <q-tooltip>{{
                    allStocks.find((stock) => stock.symbol == props.value)?.name
                  }}</q-tooltip>
                </div>
              </q-item>
            </q-td>
          </template>

          <template v-slot:body-cell="props">
            <q-td
              :props="props"
              :class="
                props.row.bulk_status === 'SELL'
                  ? 'bg-red-5 text-white'
                  : props.row.bulk_status === 'BUY'
                  ? 'bg-green-5 text-white'
                  : 'bg-blue-5 text-white'
              "
            >
              {{ props.value }}
            </q-td>
          </template>
        </q-table>
      </template>
    </PackageAuth>
    <!-- end of else -->
  </q-card>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useStore } from "vuex";
import moment from "moment";
import useData from "src/use/data";
import PackageAuth from "src/components/Packages/PackageAuth.vue";
import useUtils from "src/use/utils";
import { LocalStorage, useQuasar } from "quasar";
import StockSearch from "src/components/StockProfile/StockSearch";

const $q = useQuasar();
const store = useStore();
const { PlayersFavouriteWithDays } = useData();

const { filterSymbolData, utilsOptionSectors } = useUtils();
const emit = defineEmits(["chartSymbol"]);

const filterSymbol = ref("");
const strength = ref("All");
const isUserPlatinum = ref(false);
const isUserStaff = ref(false);
let interval;

const mySignals = ref(false);
const isMarketOn = ref(false);

const allStocks = ref([]);
const mySignalsStocks = ref([]);
const bulkTransactionData = ref([]);
const filterPlayerFavouriteBuySell = ref("show_all");

const optionSectors = ref(utilsOptionSectors());
const selectedSector = ref(optionSectors.value[0]);

async function loadLiveData() {
  $q.loading.show({});

  try {
    const resBulkTransactionData = await PlayersFavouriteWithDays(1);

    bulkTransactionData.value = resBulkTransactionData.data;
  } catch (error) {
    console.error("Error loading live data:", error);
  } finally {
    $q.loading.hide();
  }
}

const computedBulkTransactionData = computed(() => {
  const sectorId = selectedSector.value.id;

  // Extract sector symbols and create a lookup set
  const sectorSymbols = allStocks.value
    ?.filter((i) => i.sector === sectorId)
    .map((stock) => stock.symbol);

  return bulkTransactionData.value.filter((item) => {
    const isSectorMatch =
      sectorId === 0 || sectorSymbols.some((stock) => stock === item.symbol);

    const isMySignalMatch =
      !mySignals.value ||
      mySignalsStocks.value.some((stock) => stock === item.symbol);

    const isStrength =
      !isUserPlatinum.value ||
      strength.value === "All" ||
      strength.value === item.strength;

    return isSectorMatch && isStrength && isMySignalMatch;
  });
});

const finalComputedData = computed(() => {
  const result = bulkTransactionData.value.reduce((acc, item) => {
    if (!acc[item.symbol]) {
      acc[item.symbol] = {
        ...item,
        cumm_quantity_total: 0,
        amount_total: 0,
        strength_buy: null,
        strength_sell: null,
        date_buy: null,
        date_sell: null,
      };
    }

    const quantityChange =
      item.bulk_status === "BUY" ? item.cumm_quantity : -item.cumm_quantity;
    acc[item.symbol].cumm_quantity_total += quantityChange;
    acc[item.symbol].amount_total +=
      item.amount * (item.bulk_status === "BUY" ? 1 : -1);

    // Track the strength separately for BUY and SELL
    if (item.bulk_status === "BUY") {
      acc[item.symbol].date_buy = item.date;
      acc[item.symbol].strength_buy = item.strength;
    } else {
      acc[item.symbol].date_sell = item.date;
      acc[item.symbol].strength_sell = item.strength;
    }

    return acc;
  }, {});

  for (const symbol in result) {
    const item = result[symbol];
    item.rate_total =
      item.cumm_quantity_total !== 0
        ? item.amount_total / item.cumm_quantity_total
        : 0;
  }

  const data = Object.values(result).map((item) => {
    return {
      // date: item.date,
      symbol: item.symbol,
      cumm_quantity: item.cumm_quantity_total,
      amount: item.amount_total,
      rate: item.rate_total,
      bulk_status: item.cumm_quantity_total > 0 ? "BUY" : "SELL",
      date: item.cumm_quantity_total > 0 ? item.date_buy : item.date_sell,
      strength:
        item.cumm_quantity_total > 0 ? item.strength_buy : item.strength_sell,
    };
  });

  const sectorId = selectedSector.value.id;
  const sectorSymbols = allStocks.value
    ?.filter((i) => i.sector === sectorId)
    .map((stock) => stock.symbol);

  return data.filter((item) => {
    const isSectorMatch =
      sectorId === 0 || sectorSymbols.some((stock) => stock === item.symbol);

    const playerFavouriteBuy =
      filterPlayerFavouriteBuySell.value !== "player_favourite_buy" ||
      item.bulk_status === "BUY";

    const playerFavouriteSell =
      filterPlayerFavouriteBuySell.value !== "player_favourite_sell" ||
      item.bulk_status === "SELL";

    const isStrength =
      !isUserPlatinum.value ||
      strength.value === "All" ||
      strength.value === item.strength;

    const isMySignalMatch =
      !mySignals.value ||
      mySignalsStocks.value.some((stock) => stock === item.symbol);

    return (
      isSectorMatch &&
      playerFavouriteBuy &&
      playerFavouriteSell &&
      isStrength &&
      isMySignalMatch
    );
  });
});

const checkUserStatus = () => {
  const packageStatus = store.state.packages.packageStatus;
  const whoAmI = store.state.accounts.whoAmI;

  mySignalsStocks.value = packageStatus.current_package.stocks;

  isUserPlatinum.value =
    packageStatus?.current_package?.package_name === "Platinum";

  isUserStaff.value = !!whoAmI?.is_staff;
  // isUserStaff.value = Boolean(whoAmI?.is_staff);

  isMarketOn.value = store.state.market.marketStatus;
};

const fetchInitialData = async () => {
  await loadLiveData();

  allStocks.value = store.state.core.allStocks || [];
  if (!allStocks.value.length) {
    await store.dispatch("core/getAllStocks");
    allStocks.value = store.state.core.allStocks;
  }

  const packageStatus = store.state.packages.packageStatus;
  const whoAmI = store.state.accounts.whoAmI;
  const isMarketOn = store.state.market.marketStatus;

  if (!packageStatus?.current_package) {
    await store.dispatch("packages/getPackageStatus");
  }

  if (!whoAmI) {
    await store.dispatch("accounts/whoAmI");
  }

  if (!isMarketOn) {
    await store.dispatch("market/marketStatusNew");
  }

  checkUserStatus();
};

onMounted(async () => {
  await fetchInitialData();

  if (isUserStaff.value && isMarketOn.value) {
    // Run loadLiveData at intervals for staff users
    interval = setInterval(async () => {
      await loadLiveData();
    }, 60000); // Adjust the interval as needed
  }
});

// Cleanup interval on component unmount
onUnmounted(() => {
  clearInterval(interval);
});

const columns = [
  {
    name: "date",
    label: "Date",
    field: "date",
    format: (val, row) => {
      return convertDate(val);
    },
    sortable: true,
    align: "left",
  },
  {
    name: "symbol",
    label: "Symbol",
    field: "symbol",
    sortable: true,
    align: "center",
  },
  {
    name: "cumm_quantity",
    label: "Cumm Quantity",
    field: "cumm_quantity",
    sortable: true,
    align: "center",
  },
  {
    name: "rate",
    label: "Rate",
    field: "rate",
    format: (val, row) => {
      return val.toFixed(2);
    },
    sortable: true,
    align: "center",
  },
  {
    name: "amount",
    label: "Amount",
    field: "amount",
    format: (val, row) => {
      return val?.toFixed(2);
    },
    sortable: true,
    align: "center",
  },
  {
    name: "bulk_status",
    label: "Status",
    field: "bulk_status",
    sortable: true,
    align: "center",
  },
];

const columnsIsStaff = [
  ...columns,
  {
    name: "strength",
    label: "Strength",
    field: "strength",
    format: (val, row) => {
      return val;
    },
    sortable: true,
    align: "center",
  },
];

const computedColumns = computed(() => {
  return isUserStaff.value ? columnsIsStaff : columns;
});

const onClickSymbolEmit = (symbol) => {
  emit("chartSymbol", symbol);
};

const pagination = {
  sortBy: "date",
  rowsPerPage: 0,
  descending: true,
};

const convertDate = (date) => moment(date).format("YYYY-MM-DD, hh:mm A");
</script>
